export type AddressComponent = "line" | "city" | "state" | "zipcode";

const DIRECTIONALS = new Set(["N", "NE", "E", "SE", "S", "SW", "W", "NW"]);
const WORD_PATTERN = /[A-Za-z]+(?:['’][A-Za-z]+)?|[A-Za-z]*\d+[A-Za-z]*/g;

function titleCaseWord(word: string): string {
  const upper = word.toUpperCase();
  if (DIRECTIONALS.has(upper) || /\d/.test(word)) return upper;
  return word
    .toLocaleLowerCase()
    .replace(/(^|['’])([a-z])/g, (_match, prefix: string, letter: string) => {
      return `${prefix}${letter.toLocaleUpperCase()}`;
    });
}

export function formatAddressText(
  value: string | null | undefined,
  component: AddressComponent = "line",
): string {
  const normalized = (value ?? "").trim().replace(/\s+/g, " ");
  if (!normalized) return "";
  if (component === "state" || component === "zipcode") {
    return normalized.toLocaleUpperCase();
  }
  return normalized
    .replace(WORD_PATTERN, titleCaseWord)
    .replace(/\bP\.?\s*O\.?\s+Box\b/gi, "PO Box")
    .replace(
      /,\s*([A-Za-z]{2})(?=\s+\d{5}(?:-\d{4})?\b|,|$)/g,
      (_match, state: string) => `, ${state.toLocaleUpperCase()}`,
    );
}

export function normalizeAddressKey(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

export function dedupeAddressDisplayValues(
  values: readonly string[],
): string[] {
  const valuesByKey = new Map<string, string>();
  for (const value of values) {
    const display = formatAddressText(value);
    const key = normalizeAddressKey(display);
    if (!valuesByKey.has(key)) valuesByKey.set(key, display);
  }
  return [...valuesByKey.values()].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" }),
  );
}

export type AddressParts = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
};

export function formatAddressParts(
  parts: AddressParts,
  options: { linesOnly?: boolean } = {},
): string {
  const addressLines = [
    formatAddressText(parts.address1),
    formatAddressText(parts.address2),
  ].filter(Boolean);
  if (options.linesOnly) return addressLines.join(", ");

  const city = formatAddressText(parts.city, "city");
  const stateZip = [
    formatAddressText(parts.state, "state"),
    formatAddressText(parts.zipcode, "zipcode"),
  ]
    .filter(Boolean)
    .join(" ");
  return [...addressLines, city, stateZip].filter(Boolean).join(", ");
}
